import type {TableConfiguration} from 'schema/table/types';
import type {DataFormConfiguration, PreviewConfiguration} from 'schema/types';
import type {
  AnyValidation,
  Data,
  Schema,
  SeparatedParts,
  ValidationErrorLevel,
} from 'schemaDefinition/types';
import type {CodeListId} from 'services/codelists/types';
import type {ThesaurusId} from 'services/thesaurus/types';

/**
 * A single, flat schema describing a manifestation-like "Book" entity.
 *
 * Part `name`s are chosen to match field keys that already have translations
 * under `form.global.field.*` in `src/localization/locales`, so labels render
 * from the existing i18n instead of showing raw keys. See `getFieldRootKeys` /
 * `useLabelAndPlaceholder` in `src/schema` for how labels are resolved.
 *
 * The same schema instance is reused by all three demo pages (edit, preview,
 * table) to show that one schema definition drives every renderer in
 * `src/schema`. It deliberately uses a flat set of parts (no nested `schema`
 * parts) so it renders cleanly as a table as well as a form/preview.
 *
 * See `src/schemaDefinition/types/parts.ts` for all available part types.
 */
export const bookSchema: Schema = {
  name: 'demoBook',
  parts: [
    {type: 'text', name: 'mainTitle', required: true},
    {type: 'text', name: 'subTitlesValue'},
    {type: 'text', name: 'isbn'},
    {type: 'textarea', name: 'editionNote', maxLength: 2000},
    {type: 'int', name: 'edition'},
    {type: 'int', name: 'pageCount', suffix: ' p.'},
    {type: 'year', name: 'publishedYear'},
    {type: 'date', name: 'inSaleDate'},
    {type: 'codelist', name: 'languages', codelistId: 'LANGUAGE'},
    {type: 'codelist', name: 'country', codelistId: 'COUNTRY'},
    {
      type: 'thesaurus',
      name: 'genreAndForm',
      thesaurusId: 'genreandform',
      cardinality: 'multiple',
    },
    {type: 'bool', name: 'firstEdition'},
  ],
};

/**
 * Builds the parts for a variant of {@link bookSchema} where *every* field
 * carries a `validation` that the {@link sampleBook} value deliberately fails.
 *
 * The same conditions are reused for both the warning and the error demo; only
 * the `level` differs (`'warning'` renders the field amber, `'error'` red), so
 * a single factory keeps the two schemas in sync.
 *
 * Note: `bool` parts (e.g. `firstEdition`) are intentionally omitted — the
 * schema system has no validation for booleans (see `validateValuePart`), so
 * they can never be made to warn or error.
 */
const buildValidatedBookParts = (
  level: ValidationErrorLevel,
): SeparatedParts => {
  // Helper to brand each condition with the shared level + a message key.
  const v = (messageKey: string, condition: AnyValidation): AnyValidation => ({
    level,
    messageKey,
    ...condition,
  });

  return [
    {
      type: 'text',
      name: 'mainTitle',
      required: true,
      // 'Søvngjengeren' (13 chars) > recommended max of 5.
      validation: v('demo.mainTitle.tooLong', {maxLength: 5}),
    },
    {
      type: 'text',
      name: 'subTitlesValue',
      // 'En roman' (8 chars) < recommended min of 20.
      validation: v('demo.subTitle.tooShort', {minLength: 20}),
    },
    {
      type: 'text',
      name: 'isbn',
      // '9788203365867' contains no hyphens.
      validation: v('demo.isbn.noHyphens', {regex: '-', default: true}),
    },
    {
      type: 'textarea',
      name: 'editionNote',
      maxLength: 2000,
      // The note is far longer than the recommended max of 10.
      validation: v('demo.editionNote.tooLong', {maxLength: 10}),
    },
    {
      type: 'int',
      name: 'edition',
      // 2 < recommended min of 5.
      validation: v('demo.edition.tooLow', {min: 5}),
    },
    {
      type: 'int',
      name: 'pageCount',
      suffix: ' p.',
      // 384 > recommended max of 100.
      validation: v('demo.pageCount.tooHigh', {max: 100}),
    },
    {
      type: 'year',
      name: 'publishedYear',
      // 2014 is before the recommended min of 2020.
      validation: v('demo.publishedYear.tooOld', {min: 2020}),
    },
    {
      type: 'date',
      name: 'inSaleDate',
      // 2014-09-01 is before the recommended min of 2020-01-01.
      validation: v('demo.inSaleDate.tooEarly', {min: '2020-01-01'}),
    },
    {
      type: 'codelist',
      name: 'languages',
      codelistId: 'LANGUAGE',
      // 'nob' does not match the recommended 'eng'.
      validation: v('demo.languages.notEnglish', {regex: 'eng', default: true}),
    },
    {
      type: 'codelist',
      name: 'country',
      codelistId: 'COUNTRY',
      // 'NO' does not match the recommended 'US'.
      validation: v('demo.country.notUs', {regex: 'US', default: true}),
    },
    {
      type: 'thesaurus',
      name: 'genreAndForm',
      thesaurusId: 'genreandform',
      cardinality: 'multiple',
      // Only 2 terms, fewer than the recommended min of 5.
      validation: v('demo.genreAndForm.tooFew', {minLength: 5}),
    },
  ];
};

/**
 * Variant of {@link bookSchema} where every field triggers a *warning* for the
 * {@link sampleBook} value. Used by the "warnings" example in `EditPage`.
 */
export const warningBookSchema: Schema = {
  name: 'demoBookWarnings',
  parts: buildValidatedBookParts('warning'),
};

/**
 * Variant of {@link bookSchema} where every field triggers a validation
 * *error* for the {@link sampleBook} value. Used by the "errors" example in
 * `EditPage`.
 */
export const errorBookSchema: Schema = {
  name: 'demoBookErrors',
  parts: buildValidatedBookParts('error'),
};

/** Codelists referenced by the schema, loaded via `CodeListsGate`. */
export const demoCodeLists: CodeListId[] = ['LANGUAGE', 'COUNTRY'];

/** Thesauruses referenced by the schema, loaded via `ThesaurusesGate`. */
export const demoThesauruses: ThesaurusId[] = ['genreandform'];

/** Sample data for the edit and preview pages. */
export const sampleBook: Data = {
  id: 'book-1',
  mainTitle: 'Søvngjengeren',
  subTitlesValue: 'En roman',
  isbn: '9788203365867',
  editionNote: 'Ny utgave med forord av forfatteren.',
  edition: 2,
  pageCount: 384,
  publishedYear: 2014,
  inSaleDate: '2014-09-01',
  languages: 'nob',
  country: 'NO',
  genreAndForm: ['338', '412'],
  firstEdition: false,
};

/** Sample rows for the table page. */
export const sampleBooks: Data[] = [
  sampleBook,
  {
    id: 'book-2',
    mainTitle: 'The Sleepwalker',
    subTitlesValue: 'A Novel',
    isbn: '9780857058287',
    editionNote: 'English translation.',
    edition: 1,
    pageCount: 352,
    publishedYear: 2018,
    inSaleDate: '2018-03-15',
    languages: 'eng',
    country: 'GB',
    genreAndForm: ['412'],
    firstEdition: true,
  },
  {
    id: 'book-3',
    mainTitle: 'Havboka',
    isbn: '9788249515356',
    edition: 1,
    pageCount: 480,
    publishedYear: 2015,
    inSaleDate: '2015-10-20',
    languages: 'nob',
    country: 'NO',
    genreAndForm: ['338'],
    firstEdition: true,
  },
];

/** Configuration for the edit form. */
export const editConfiguration: Partial<DataFormConfiguration> = {
  showWhenReadonlyAndEmpty: true,
};

/** Configuration for the preview. */
export const previewConfiguration: PreviewConfiguration = {
  layout: {
    showEmptyValues: false,
  },
};

/** Configuration for the table, defining which columns are shown. */
export const tableConfiguration: TableConfiguration = {
  defaultColumns: [
    {name: 'mainTitle', visible: true},
    {name: 'subTitlesValue', visible: true},
    {name: 'isbn', visible: true},
    {name: 'edition', visible: true},
    {name: 'pageCount', visible: true},
    {name: 'publishedYear', visible: true},
    {name: 'inSaleDate', visible: true},
    {name: 'languages', visible: true},
    {name: 'country', visible: true},
    {name: 'genreAndForm', visible: true},
    {name: 'firstEdition', visible: true},
  ],
};
